import React, { useEffect, useState } from 'react';

import ViewFileImg from '../../images/SideBar/viewFile';
import { IFromFieldSet, IInput, IViewTag, SwitchProps } from './props';

export const Input = React.forwardRef<HTMLInputElement, IInput>(({ 
  key,
  name,
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  required = false,
  disabled = false,
  passwordValue,
}, ref) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);
  useEffect(() => {
    if (touched) {
      validateInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched]);

  const validateInput = () => {
    let errorMessage = null;

    if (
      name === "fullName" ||
      name === "stationName" ||
      name === "emergencyContactName" ||
      name === "supervisorName"
    ) {
      const words = String(value ?? '').trim().split(" ");
      if (
        words.length < 2 ||
        !words.every((word: string) => /^[A-Z]/.test(word))
      ) {
        errorMessage =
          "There must be at least two words, and each word should start with a capital letter.";
      }
    } else if (
      name === "phoneNo" ||
      name === "emergencyContactPhoneNo" ||
      name === "supervisorContact"
    ) {
      if (!/^[6-9]\d{9}$/.test(String(value ?? ''))) {
        errorMessage =
          "The phone number must start with a digit between 6 and 9 and be exactly 10 digits long.";
      }
    } else if (name === "zipCode") {
      if (!/^[1-9]\d{5}$/.test(String(value ?? ''))) {
        errorMessage =
          "Zip Code must start with a digit between 1 and 9 and be exactly 6 digits long.";
      }
    } else if (name === "password") {
      const val = String(value ?? '');
      if (val.length < 8) {
        errorMessage = "Password must be at least 8 characters long.";
      } else if (!/[A-Z]/.test(val)) {
        errorMessage = "Password must contain at least one uppercase letter.";
      } else if (!/[a-z]/.test(val)) {
        errorMessage = "Password must contain at least one lowercase letter.";
      } else if (!/[0-9]/.test(val)) {
        errorMessage = "Password must contain at least one digit.";
      }
    } else if (name === "conformPassword") {
      if (passwordValue !== value) {
        errorMessage = "Password and Confirm Password do not match.";
      }
    }
    setError(errorMessage);
  };

  const handleInternalBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    validateInput();
    if (onBlur) onBlur(e);
  };

  return (
    <div key={key} className="mb-4">
      <label className="text-fontBlack text-xs mb-2 flex" htmlFor={name}>
        {label}:{required ? <p className="text-red-600">*</p> : ""}
      </label>
      <input
        className={`appearance-none border h-14 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
          error ? "border-red-600" : "border-border"
        }`}
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={handleInternalBlur}
        required={required}
        disabled={disabled}
        ref={ref}
      />
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
});
Input.displayName = 'Input';

export const DocUploader: React.FC<IInput> = ({
  name,
  label,
  type = "file",
  value,
  onChange,
  required = false,
}) => {
  return (
    <div>
      <label className="text-fontBlack text-sm mb-2 flex" htmlFor={name}>
        {label}
        {required ? <p className="text-red-600">*</p> : ""}
      </label>
      <input
        className="block w-full border border-border  h-14 rounded file:m-3 file:bg-white file:shadow-none file:w-20 fi file:h-8 file:border file:border-gray-400 file:text-fontColor file:rounded  file:text-xs file:font-semibold "
        type={type}
        accept="application/pdf"
        id={name}
        onChange={onChange}
        required={required}
      />
    </div>
  );
};
export const FormFieldset: React.FC<IFromFieldSet> = ({
  Ftitle,
  Fstyle,
  FLstlye,
  children,
}) => {
  return (
    <fieldset className={` m-2 ${Fstyle}`}>
      <legend className={` ml-4 font-bold text-fontBlack text-base ${FLstlye}`}>
        {Ftitle}
      </legend>
      {children}
    </fieldset>
  );
};

const Switch: React.FC<SwitchProps> = ({ id, name, checked, onChange }) => {
  return (
    <label className="flex cursor-pointer select-none items-center">
      <div className="relative">
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`block h-8 w-14 rounded-full ${
            checked ? "bg-primary" : "bg-black"
          }`}
        ></div>
        <div
          className={`absolute left-1 top-1 h-6 w-6 bg-white rounded-full transition-transform ${
            checked ? "transform translate-x-full" : ""
          }`}
        ></div>
      </div>
    </label>
  );
};

export default Switch;

export const ViewTag: React.FC<IViewTag> = ({ link }) => {
  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      <button
        className="hover:text-fontColLit6 flex items-center"
        type="button"
      >
        <ViewFileImg />
        <span className="ml-2">View</span>
      </button>
    </a>
  );
};

export const Textarea: React.FC<IInput> = ({
  key,
  name,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  passwordValue,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);
  useEffect(() => {
    if (touched) {
      validateInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched]);

  const validateInput = () => {
    let errorMessage = null;

    if (
      name === "fullName" ||
      name === "stationName" ||
      name === "emergencyContactName" ||
      name === "supervisorName"
    ) {
      const words = value.trim().split(" ");
      if (
        words.length < 2 ||
        !words.every((word: string) => /^[A-Z]/.test(word))
      ) {
        errorMessage =
          "There must be at least two words, and each word should start with a capital letter.";
      }
    } else if (
      name === "phoneNo" ||
      name === "emergencyContactPhoneNo" ||
      name === "supervisorContact"
    ) {
      if (!/^[6-9]\d{9}$/.test(value)) {
        errorMessage =
          "The phone number must start with a digit between 6 and 9 and be exactly 10 digits long.";
      }
    } else if (name === "zipCode") {
      if (!/^[1-9]\d{5}$/.test(value)) {
        errorMessage =
          "Zip Code must start with a digit between 1 and 9 and be exactly 6 digits long.";
      }
    } else if (name === "password") {
      if (value.length < 8) {
        errorMessage = "Password must be at least 8 characters long.";
      } else if (!/[A-Z]/.test(value)) {
        errorMessage = "Password must contain at least one uppercase letter.";
      } else if (!/[a-z]/.test(value)) {
        errorMessage = "Password must contain at least one lowercase letter.";
      } else if (!/[0-9]/.test(value)) {
        errorMessage = "Password must contain at least one digit.";
      }
    } else if (name === "conformPassword") {
      if (passwordValue !== value) {
        errorMessage = "Password and Confirm Password do not match.";
      }
    }
    setError(errorMessage);
  };

  const handleBlur = () => {
    setTouched(true);
    validateInput();
  };

  return (
    <div key={key} className="mb-4">
      <label className="text-fontBlack text-xs mb-2 flex" htmlFor={name}>
        {label}:{required ? <p className="text-red-600">*</p> : ""}
      </label>
      <textarea
        className={`appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
          error ? "border-red-600" : "border-border"
        }`}
        id={name}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
        rows={5}
      />
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
};
